import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface UnsyncedCustomerEmailItem {
  id: string;
  clientName?: string;
  clientEmail: string;
  emailSubject: string;
  emailBody: string;
  /** JSON string of dynamic_template_data sent to SendGrid (when available). */
  emailTemplateData?: string;
  /** Rendered HTML of the email (actual SendGrid output), when available. */
  emailBodyHtml?: string;
  /** Rendered plain text of the email, when available. */
  emailBodyText?: string;
  createdAt: string;
  studioId?: string;
  locationId?: string;
}

export interface UnsyncedCustomerEmailsResponse {
  data: UnsyncedCustomerEmailItem[];
  total: number;
  page: number;
  limit: number;
}

export const getUnsyncedCustomerEmailsAPI = async (params: {
  page?: number;
  limit?: number;
}): Promise<UnsyncedCustomerEmailsResponse> => {
  const response = await baseAPI.get<UnsyncedCustomerEmailsResponse>(
    'api/customer-emails/unsynced',
    {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      },
    },
  );
  return response.data;
};

export const useUnsyncedCustomerEmails = (params: {
  page: number;
  limit: number;
}) => {
  return useQuery({
    queryKey: [KEYS.UNSYNCED_CUSTOMER_EMAILS, params.page, params.limit],
    queryFn: () => getUnsyncedCustomerEmailsAPI(params),
    enabled: params.page >= 1 && params.limit >= 1,
  });
};

export const syncUnsyncedEmailToGhlAPI = async (id: string): Promise<void> => {
  await baseAPI.post(`api/customer-emails/unsynced/${id}/sync-to-ghl`);
};

export const useSyncUnsyncedEmailToGhl = (params: {
  page: number;
  limit: number;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncUnsyncedEmailToGhlAPI(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.UNSYNCED_CUSTOMER_EMAILS],
      });
    },
  });
};
